import { isNullish } from '@sas/nova-core/typeguards';
import { createUseTextLocalizer } from '../../../../../locale/createUseTextLocalizer';
import { isIconColumn } from '../../utils/typeguards';
import { getCellHelpPopup, getCellIcon, getCellMenuButton } from '../../utils/utils';
import { ROLE_TEMPLATE_LITERAL } from './types';
import bundle from '../../../NovaComponents-List-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);

/**
 * @internal
 */

/**
 * @internal
 */
export const useSayCell = props => {
  const {
    columns,
    startColIndex: startIndex,
    getItemType,
    getRowKey,
    getItemProps,
    getItemGroup,
    disabled,
    isItemDisabled,
    narration,
    readOnly,
    isItemReadOnly
  } = props;
  const localize = useTextLocalizer();
  return params => {
    const colIndex = params.current.colIndex - startIndex;
    const rowIndex = params.current.rowIndex ?? -1;
    const prevRowIndex = params.previous?.rowIndex ?? -1;
    const column = columns[colIndex];
    const prevItemType = params.previous ? getItemType(prevRowIndex) : '';
    const itemType = getItemType(rowIndex);
    const isSelectionCell = colIndex < 0;
    const rowKey = getRowKey?.(rowIndex);
    const readOnlyRow = !!readOnly || !!(rowKey && isItemReadOnly?.(rowKey));
    const disabledRow = !!disabled || !!(rowKey && isItemDisabled?.(rowKey));
    const hasRowChanged = rowIndex !== prevRowIndex;
    const parts = (itemType === 'groupItem' || itemType !== 'invalid' && !isSelectionCell) && params.default ? [params.default] : [];

    // we entered from outside the list, so announce the group if there is one
    if (prevRowIndex === -1) {
      const groupName = getItemGroup?.(rowIndex);
      if (groupName) {
        parts.unshift(makeGroupSayCell(localize));
        parts.unshift(groupName);
      }
    }
    // we entered a new group from below, so announce the new group and 'group start'
    else if (prevRowIndex > rowIndex && prevItemType === 'groupItem' && itemType !== 'groupItem') {
      parts.unshift(makeGroupStartSayCell(localize));
      parts.unshift(getItemGroup?.(rowIndex) ?? '');
    }
    const itemProps = getItemProps?.(rowIndex);
    const isUnread = (itemType === 'item' || itemType === 'linkItem') && !isNullish(itemProps) && 'unread' in itemProps && itemProps.unread === true;
    if (hasRowChanged && isUnread) {
      parts.unshift(localize('unread.aria'));
    }
    if (itemType === 'groupItem') {
      // announce that this is a group header
      parts.push(makeGroupHeaderSayCell(localize));
      // if we get to this group header from below, don't announce 'group start'
      // because you're already in that group
      if (prevRowIndex !== -1 && prevRowIndex < rowIndex) parts.push(makeGroupStartSayCell(localize));
    } else if (column && isIconColumn(column)) {
      const cellHelpPopup = getCellHelpPopup(column, rowIndex);
      const cellMenuButton = getCellMenuButton(column, rowIndex);
      const cellIcon = getCellIcon(column, rowIndex);
      if (cellMenuButton && !readOnlyRow) {
        // button menu role
        parts.push(makeItemMenuButtonIconSayCell(localize, !!disabledRow || !!column.disabled?.(rowIndex)));
      } else if (cellHelpPopup && (!readOnlyRow || readOnlyRow && column.displayHelpPopupReadOnly?.(rowIndex))) {
        // button role
        parts.push(makeItemButtonIconSayCell(localize, !!disabledRow || !!column.disabled?.(rowIndex)));
      } else if (cellIcon) {
        if (!!column.onPress) {
          if (!readOnlyRow) {
            // button role
            parts.push(makeItemButtonIconSayCell(localize, !!disabledRow || !!column.disabled?.(rowIndex)));
          }
        } else {
          // graphic role
          parts.push(makeItemStaticIconSayCell(localize));
        }
      }
    } else if (itemType === 'linkItem' && !readOnly) {
      const role = makeLinkItemSayCell(localize);
      const toActivate = makeLinkItemActivateSayCell(localize);
      if (itemProps?.description) {
        parts.forEach((part, index) => {
          parts[index] = part.replace(ROLE_TEMPLATE_LITERAL, `${role}, ${toActivate}`);
        });
      } else {
        parts.push(role);
        parts.push(toActivate);
      }
    }
    const text = parts.join(', ');

    // give consumer an option to append or modify announcement
    if (typeof narration?.onSayCell === 'function') {
      return narration.onSayCell({
        current: params.current,
        previous: params.previous,
        default: text
      });
    }
    return text;
  };
};

/**
 * @internal
 */
const makeItemMenuButtonIconSayCell = (localize, disabled) => {
  // a role
  const parts = [localize('icon.menuButton.aria')];
  if (!disabled) {
    // a keyboard action
    parts.push(localize('icon.menuButtonToActivate.aria'));
  }
  return parts.join(', ');
};

/**
 * @internal
 */
const makeItemButtonIconSayCell = (localize, disabled) => {
  // a role
  const parts = [localize('icon.button.aria')];
  if (!disabled) {
    // a keyboard action
    parts.push(localize('icon.buttonToActivate.aria'));
  }
  return parts.join(', ');
};

/**
 * @internal
 */
const makeItemStaticIconSayCell = localize => localize('icon.graphic.aria');

/**
 * @internal
 */
const makeLinkItemSayCell = localize => {
  return localize('linkItem.aria');
};

/**
 * @internal
 */
const makeLinkItemActivateSayCell = localize => {
  return localize('linkItem.toActivate.aria');
};

/**
 * @internal
 */
const makeGroupSayCell = localize => {
  return localize('group.aria');
};

/**
 * @internal
 */
const makeGroupHeaderSayCell = localize => {
  return localize('groupHeader.aria');
};

/**
 * @internal
 */
const makeGroupStartSayCell = localize => {
  return localize('groupStart.aria');
};
//# sourceMappingURL=useSayCell.js.map